import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { Observable } from "rxjs";
import { AuthService } from "./auth.service";
import { EnhancedMenuService } from "../enhanced-menu.service";

@Injectable({
  providedIn: "root",
})
export class PrivilagesGuard implements CanActivate, CanActivateChild {
  constructor(
    public router: Router,
    private authServ: AuthService,
    private enhancedMenuService: EnhancedMenuService
  ) { }

  async canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.canActivate(childRoute, state);
  }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    const user = this.authServ.getLoggedInUser();
    if (!user) {
      this.router.navigate(['/login']);
      return false;
    }

    const currentPath = (state.url || '').split('?')[0].trim().toLowerCase();

    // Universal public routes within full layout (no special module permission required)
    const universalPaths = [
      '/dashboard',
      '/profile',
      '/notification',
      '/activateaccount',
      '/chat'
    ];

    if (universalPaths.some(p => currentPath === p || currentPath.startsWith(p + '/'))) {
      return true;
    }

    // Role 1 (SuperAdmin) has full system privileges
    const currentRoleId = user.currentRoleId || user.roleIds?.[0];

    // Strict SuperAdmin routes: only SuperAdmin (Role 1) can access any superadmin URL
    if (currentPath.includes('/superadmin/') || currentPath.endsWith('/superadmin')) {
      if (currentRoleId !== 1) {
        console.warn(`[PrivilagesGuard] Blocked non-superadmin (role: ${currentRoleId}) from superadmin route: ${currentPath}`);
        this.router.navigate(['/dashboard/dashboard']);
        return false;
      }
      return true;
    }

    // Role-specific route boundaries
    if (currentPath.includes('/checker/') || currentPath.endsWith('/checker')) {
      if (currentRoleId !== 4 && currentRoleId !== 1) {
        console.warn(`[PrivilagesGuard] Blocked unauthorized role (${currentRoleId}) from checker route: ${currentPath}`);
        this.router.navigate(['/dashboard/dashboard']);
        return false;
      }
    }

    if (currentPath.includes('/maker/') || currentPath.endsWith('/maker')) {
      if (currentRoleId !== 5 && currentRoleId !== 1) {
        console.warn(`[PrivilagesGuard] Blocked unauthorized role (${currentRoleId}) from maker route: ${currentPath}`);
        this.router.navigate(['/dashboard/dashboard']);
        return false;
      }
    }

    if (currentPath.includes('/approver/') || currentPath.endsWith('/approver')) {
      if (currentRoleId !== 3 && currentRoleId !== 1) {
        console.warn(`[PrivilagesGuard] Blocked unauthorized role (${currentRoleId}) from approver route: ${currentPath}`);
        this.router.navigate(['/dashboard/dashboard']);
        return false;
      }
    }

    if (currentPath.includes('/assigner/') || currentPath.endsWith('/assigner')) {
      if (currentRoleId !== 2 && currentRoleId !== 1) {
        console.warn(`[PrivilagesGuard] Blocked unauthorized role (${currentRoleId}) from assigner route: ${currentPath}`);
        this.router.navigate(['/dashboard/dashboard']);
        return false;
      }
    }

    if (currentRoleId === 1) {
      return true;
    }

    // Dynamic role privileges from session or backend
    let privileges = this.authServ.getVerticalRoutes();
    if (!privileges || privileges.length === 0) {
      try {
        privileges = await this.enhancedMenuService.getFilteredMenu();
      } catch (e) {
        privileges = [];
      }
    }

    const hasAccess = this.checkAccess(privileges, currentPath);

    if (hasAccess) {
      return true;
    } else {
      console.warn(`[PrivilagesGuard] Access denied for user role ${currentRoleId} on path: ${currentPath}`);
      this.router.navigate(['/dashboard/dashboard']);
      return false;
    }
  }

  private checkAccess(privileges: any[], currentPath: string): boolean {
    if (!privileges || !Array.isArray(privileges) || privileges.length === 0) {
      return false;
    }
    const normalizedTarget = currentPath.trim().toLowerCase();

    return privileges.some(privilege => {
      const pPath = (privilege?.path || '').trim().toLowerCase();
      if (pPath && (pPath === normalizedTarget || (normalizedTarget.startsWith(pPath + '/') && pPath !== '/'))) {
        return true;
      }
      if (privilege.submenu && Array.isArray(privilege.submenu)) {
        return privilege.submenu.some((submenuItem: any) => {
          const sPath = (submenuItem?.path || '').trim().toLowerCase();
          return sPath && (sPath === normalizedTarget || (normalizedTarget.startsWith(sPath + '/') && sPath !== '/'));
        });
      }
      return false;
    });
  }
}

